import { Injectable } from '@angular/core';
import { NgForage } from 'ngforage';
import { Observable, Subject } from 'rxjs';
import * as semaphore from 'semaphore';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import DateHelper from 'src/app/helpers/date-helper';
import SemaphoreHelper from 'src/app/helpers/semaphore-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import { ImmoTask } from 'src/app/model/immo-task/immo-task';
import { SubProject } from 'src/app/model/project/sub-project';
import { Tour } from 'src/app/model/tour/tour';
import { TourItem } from 'src/app/model/tour/tour-item';
import { ImmoTaskEditFunction } from 'src/app/pages/immo-ip/placer/tour-item-detail/immo-task/helper/immo-ip-placer-immo-task-functions';
import { ConnectionStatusService } from 'src/app/services/connection-status.service';
import { ImmoIPImmoTaskChangesService } from 'src/app/services/immo-ip-placer-offline-changes/immo-ip-immo-task-changes/immo-ip-immo-task-changes.service';
import { ImmoIPProductChangesService } from '../../../../../services/immo-ip-placer-offline-changes/immo-ip-product-changes/immo-ip-product-changes.service';
import { ImmoIPSubProjectChangesService } from '../../../../../services/immo-ip-placer-offline-changes/immo-ip-sub-project-changes/immo-ip-sub-project-changes.service';
import { NgForageTimestampService } from '../../../../../services/ng-forage/ng-forage-timestamp.service';
import { OfflineStorageTourHelper } from './helper/offline-storage-tour-helper';
import { OfflineStorageReadControllerRequestService } from './online-service/offline-storage-read-controller-request.service';
import { ProjectToOtherTour } from './project-to-other-tour-storage/project-to-other-tour';
import { ProjectToOtherTourStorageService } from './project-to-other-tour-storage/project-to-other-tour-storage.service';
import { ProjectToTour } from './project-to-tour-storage/project-to-tour';
import { ProjectToTourStorageService } from './project-to-tour-storage/project-to-tour-storage.service';

type BaseSubProjectSearchResult = {
  discriminator: string;
};
type DateStringAndSubProject = BaseSubProjectSearchResult & {
  discriminator: 'DateStringAndSubProject';
  dateString: string;
  subProjectId: number;
};
type SubProjectInOtherTour = BaseSubProjectSearchResult & {
  discriminator: 'SubProjectInOtherTour';
  projectId: number;
};

@Injectable({
  providedIn: 'root',
})
export class OfflineStorageTourService {
  private getFromOnline: boolean = true;
  private expiredTourRemover: ExpiredTourRemover;

  static readonly storageKeyPrefix = 'tours-';
  static readonly timestampCollectionKey = 'tours';
  static readonly offlineStorageMutex = semaphore(1);

  private readonly subProjectHasChangedSubject =
    new Subject<SubProjectWithChangeType>();
  private readonly immoTaskHasChangedSubject =
    new Subject<ImmoTaskWithChangeType>();

  constructor(
    private connectionStatusService: ConnectionStatusService,
    private ngForageTimestampService: NgForageTimestampService,
    private offlineStorageReadControllerRequestService: OfflineStorageReadControllerRequestService,
    private ngForage: NgForage,
    private immoIPSubProjectChangesService: ImmoIPSubProjectChangesService,
    private immoIPProductChangesService: ImmoIPProductChangesService,
    private immoIPImmoTaskChangesService: ImmoIPImmoTaskChangesService,
    private projectToTourStorageService: ProjectToTourStorageService,
    private projectToOtherTourStorageService: ProjectToOtherTourStorageService
  ) {
    this.connectionStatusService
      .getConnectionStatusSubject()
      .subscribe((online: boolean) => {
        this.getFromOnline = online;
      });
    this.expiredTourRemover = new ExpiredTourRemover(
      this.ngForageTimestampService,
      this.ngForage,
      this.projectToTourStorageService
    );
    this.expiredTourRemover.removeExpiredItems();
  }

  static getStorageKeyFor(dateString: string): string {
    if (StringHelper.isEmpty(dateString)) {
      throw new Error('dateString must not be null or empty');
    }
    return `${this.storageKeyPrefix}${dateString}`;
  }

  public getTour(dateString: string): Promise<Tour> {
    if (StringHelper.isEmpty(dateString)) {
      throw new Error('dateString must not be null or empty');
    }
    return this.getFromOnline
      ? this.getTourFromOnline(dateString)
      : this.getTourFromOffline(dateString);
  }

  private async getTourFromOnline(dateString: string): Promise<Tour> {
    const tour =
      await this.offlineStorageReadControllerRequestService.offlineStorageReadTour(
        dateString
      );
    if (tour != null) {
      this.storeTour(tour, dateString);
    }
    return tour;
  }

  private storeTour(tour: Tour, dateString: string): Promise<void> {
    return SemaphoreHelper.use(
      OfflineStorageTourService.offlineStorageMutex,
      async () => {
        await this.ngForage.setItem(
          OfflineStorageTourService.getStorageKeyFor(dateString),
          tour
        );
        await this.storeProjectToTourObjects(tour, dateString);
        this.ngForageTimestampService.setTimestamps(
          OfflineStorageTourService.timestampCollectionKey,
          [{ key: dateString, timestamp: Date.now() }]
        );
      }
    );
  }

  private async storeProjectToTourObjects(
    tour: Tour,
    dateString: string
  ): Promise<void> {
    if (!CollectionHelper.isPopulated(tour.tourItems)) {
      return;
    }
    const promises = [];
    for (const tourItem of tour.tourItems) {
      if (
        tourItem.subProject != null &&
        tourItem.subProject.project != null &&
        tourItem.subProject.id != null &&
        tourItem.subProject.project.id != null
      ) {
        promises.push(
          this.projectToTourStorageService.storeObject({
            id: tourItem.subProject.project.id,
            dateString: dateString,
            subProjectId: tourItem.subProject.id,
          })
        );
      }
    }
    if (promises.length > 1) {
      await Promise.all(promises);
    }
  }

  private async getTourFromOffline(dateString: string): Promise<Tour> {
    const toReturn = await this.ngForage.getItem<Tour>(
      OfflineStorageTourService.getStorageKeyFor(dateString)
    );
    if (toReturn != null) {
      this.updateTimestamp(dateString);
      if (CollectionHelper.isPopulated(toReturn.tourItems)) {
        await this.applyOfflineTourChanges(toReturn.tourItems);
      }
    }
    return toReturn;
  }

  private async applyOfflineTourChanges(tourItems: TourItem[]): Promise<void> {
    const subProjects: SubProject[] = [];
    const immoTasks: ImmoTask[] = [];
    tourItems.forEach((ti) => {
      if (ti.subProject != null) {
        subProjects.push(ti.subProject);
      } else if (ti.immoTask != null) {
        immoTasks.push(ti.immoTask);
      }
    });

    if (subProjects.length > 0 || immoTasks.length > 0) {
      await Promise.all([
        subProjects.length === 0
          ? Promise.resolve()
          : OfflineStorageTourHelper.applySubProjectChanges(
              subProjects,
              this.immoIPSubProjectChangesService,
              this.immoIPProductChangesService
            ),
        immoTasks.length === 0
          ? Promise.resolve()
          : OfflineStorageTourHelper.applyImmoTaskChanges(
              immoTasks,
              this.immoIPImmoTaskChangesService
            ),
      ]);
    }
  }

  public async retrieveTour(dateString: string): Promise<Tour> {
    const toReturn = await this.ngForage.getItem<Tour>(
      OfflineStorageTourService.getStorageKeyFor(dateString)
    );
    if (toReturn != null) {
      this.updateTimestamp(dateString);
    }
    return toReturn;
  }

  public async updateTourIfExists<T>(
    dateString: string,
    update: (tour: Tour, updateObject: T) => void,
    updateObject: T
  ): Promise<void> {
    const tour = await this.retrieveTour(dateString);
    if (tour != null) {
      update(tour, updateObject);
      await this.storeTour(tour, dateString);
    }
  }

  public async getSubProject(
    dateString: string,
    subProjectId: number,
    projectId: number
  ): Promise<SubProject> {
    const toReturn = this.getFromOnline
      ? await this.getSubProjectFromOnline(dateString, subProjectId)
      : await this.getSubProjectFromOffline(dateString, subProjectId, null);
    if (toReturn != null) {
      await OfflineStorageTourHelper.applySubProjectChanges(
        [toReturn],
        this.immoIPSubProjectChangesService,
        this.immoIPProductChangesService
      );
    }
    return toReturn;
  }

  private async getSubProjectFromOnline(
    dateString: string,
    subProjectId: number
  ): Promise<SubProject> {
    const subProject =
      await this.offlineStorageReadControllerRequestService.offlineStorageSubProject(
        subProjectId
      );
    if (subProject != null) {
      SemaphoreHelper.use(
        OfflineStorageTourService.offlineStorageMutex,
        async () => {
          const storageKey =
            OfflineStorageTourService.getStorageKeyFor(dateString);
          const tour = await this.ngForage.getItem<Tour>(storageKey);
          if (tour != null && CollectionHelper.isPopulated(tour.tourItems)) {
            let subProjectReplaced = false;
            for (const tourItem of tour.tourItems) {
              if (
                tourItem.subProject != null &&
                tourItem.subProject.id === subProject.id
              ) {
                tourItem.subProject = subProject;
                subProjectReplaced = true;
                break;
              }
            }
            if (subProjectReplaced) {
              await this.ngForage.setItem(storageKey, tour);
              await this.ngForageTimestampService.setTimestamps(
                OfflineStorageTourService.timestampCollectionKey,
                [{ key: dateString, timestamp: Date.now() }]
              );
            }
          }
        }
      );
    }
    return subProject;
  }

  async getSubProjectFromOffline(
    dateString: string,
    subProjectId: number,
    projectId: number
  ): Promise<SubProject> {
    const tour = await this.ngForage.getItem<Tour>(
      OfflineStorageTourService.getStorageKeyFor(dateString)
    );

    let toReturn = null;

    if (tour != null && CollectionHelper.isPopulated(tour.tourItems)) {
      for (const tourItem of tour.tourItems) {
        if (
          tourItem.subProject != null &&
          tourItem.subProject.id === subProjectId
        ) {
          toReturn = tourItem.subProject;
          break;
        }
      }
    }

    if (toReturn != null) {
      this.updateTimestamp(dateString);
    } else {
      const projectToOtherTour =
        await this.projectToOtherTourStorageService.getObject(projectId);
      toReturn =
        projectToOtherTour == null ||
        projectToOtherTour.subProject == null ||
        projectToOtherTour.subProject.id !== subProjectId
          ? null
          : projectToOtherTour.subProject;
    }

    return toReturn;
  }

  private async getProjectToOtherTourForSubProject(
    subProjectId: number,
    projectId: number
  ): Promise<ProjectToOtherTour> {
    const projectToOtherTour =
      await this.projectToOtherTourStorageService.getObject(projectId);
    return projectToOtherTour == null ||
      projectToOtherTour.subProject == null ||
      projectToOtherTour.subProject.id !== subProjectId
      ? null
      : projectToOtherTour;
  }

  private async getSubProjectWithTourFromOffline(
    dateString: string,
    subProjectId: number
  ): Promise<SubProjectWithTour> {
    const tour = await this.ngForage.getItem<Tour>(
      OfflineStorageTourService.getStorageKeyFor(dateString)
    );

    if (tour == null || !CollectionHelper.isPopulated(tour.tourItems)) {
      return null;
    }

    let toReturn = null;

    for (const tourItem of tour.tourItems) {
      if (
        tourItem.subProject != null &&
        tourItem.subProject.id === subProjectId
      ) {
        toReturn = tourItem.subProject;
        break;
      }
    }

    if (toReturn != null) {
      this.updateTimestamp(dateString);
    }

    return { subProject: toReturn, tour: tour };
  }

  public getProjectToTour(
    projectId: number
  ): Promise<DateStringAndSubProject | SubProjectInOtherTour> {
    if (projectId == null) {
      throw new Error('projectId must not be null');
    }
    return this.getFromOnline
      ? this.getProjectToTourFromOnline(projectId)
      : this.getProjectToTourFromOffline(projectId);
  }

  private async getProjectToTourFromOnline(
    projectId: number
  ): Promise<DateStringAndSubProject | SubProjectInOtherTour> {
    try {
      const tourForProject =
        await this.offlineStorageReadControllerRequestService.offlineStorageReadTourForProject(
          projectId
        );

      if (tourForProject.tour != null) {
        const toReturn: DateStringAndSubProject = {
          discriminator: 'DateStringAndSubProject',
          dateString: DateHelper.formatDate(
            tourForProject.tour.date,
            'yyyyMMdd'
          ),
          subProjectId: tourForProject.subProjectId,
        };
        await this.storeTour(tourForProject.tour, toReturn.dateString);
        return toReturn;
      } else {
        const toReturn: SubProjectInOtherTour = {
          discriminator: 'SubProjectInOtherTour',
          projectId: projectId,
        };
        await this.projectToOtherTourStorageService.storeObject({
          id: projectId,
          subProject: tourForProject.subProjectInOtherTour.subProject,
          tourDateString: DateHelper.formatDate(
            tourForProject.subProjectInOtherTour.tourDate == null
              ? new Date()
              : tourForProject.subProjectInOtherTour.tourDate,
            'yyyyMMdd'
          ),
          user: tourForProject.subProjectInOtherTour.placerUser,
        });
        return toReturn;
      }
    } catch (err) {
      if (err.status === 404) {
        await this.projectToTourStorageService.removeObjectForID(projectId);
      }
      throw err;
    }
  }

  private async getProjectToTourFromOffline(
    projectId: number
  ): Promise<DateStringAndSubProject | SubProjectInOtherTour> {
    const projectToTourPromise =
      this.projectToTourStorageService.getObject(projectId);
    const projectToOtherTourPromise =
      this.projectToOtherTourStorageService.getObject(projectId);
    const [projectToTour, projectToOtherTour] = await Promise.all([
      projectToTourPromise,
      projectToOtherTourPromise,
    ]);

    if (projectToTour == null && projectToOtherTour == null) {
      throw { status: 404 };
    }

    if (projectToTour == null) {
      return {
        discriminator: 'SubProjectInOtherTour',
        projectId: projectId,
      };
    } else if (projectToOtherTour == null) {
      return {
        discriminator: 'DateStringAndSubProject',
        dateString: projectToTour.dateString,
        subProjectId: projectToTour.subProjectId,
      };
    } else {
      if (projectToOtherTour.subProject.id < projectToTour.subProjectId) {
        return {
          discriminator: 'DateStringAndSubProject',
          dateString: projectToTour.dateString,
          subProjectId: projectToTour.subProjectId,
        };
      } else {
        return {
          discriminator: 'SubProjectInOtherTour',
          projectId: projectId,
        };
      }
    }
  }

  private updateTimestamp(dateString: string): void {
    SemaphoreHelper.use(
      OfflineStorageTourService.offlineStorageMutex,
      async () => {
        await this.ngForageTimestampService.setTimestamps(
          OfflineStorageTourService.timestampCollectionKey,
          [{ key: dateString, timestamp: Date.now() }]
        );
      }
    );
  }

  public async saveSubProjectChangeAndEmit<ParameterObject>(
    dateString: string,
    subProjectId: number,
    projectId: number,
    changeType: ChangeType,
    param: ParameterObject,
    changeFunction: (s: SubProject, param: ParameterObject) => void
  ): Promise<void> {
    const subProjectWithTourPromise = this.getSubProjectWithTourFromOffline(
      dateString,
      subProjectId
    );
    const projectToOtherTourPromise = this.getProjectToOtherTourForSubProject(
      subProjectId,
      projectId
    );
    const [subProjectWithTour, projectToOtherTour] = await Promise.all([
      subProjectWithTourPromise,
      projectToOtherTourPromise,
    ]);

    if (
      subProjectWithTour != null &&
      subProjectWithTour.subProject != null &&
      subProjectWithTour.tour != null
    ) {
      this.applyChangeToOwnTourSubProject(
        dateString,
        subProjectWithTour,
        changeType,
        param,
        changeFunction
      );
    } else if (
      projectToOtherTour != null &&
      projectToOtherTour.subProject != null &&
      projectToOtherTour.subProject.project != null &&
      projectToOtherTour.subProject.project.id != null
    ) {
      this.applyChangeToOtherTourSubProject(
        projectToOtherTour,
        changeType,
        param,
        changeFunction
      );
    }
  }

  private applyChangeToOwnTourSubProject<ParameterObject>(
    dateString: string,
    subProjectWithTour: SubProjectWithTour,
    changeType: ChangeType,
    param: ParameterObject,
    changeFunction: (s: SubProject, param: ParameterObject) => void
  ): void {
    changeFunction(subProjectWithTour.subProject, param);
    SemaphoreHelper.use(
      OfflineStorageTourService.offlineStorageMutex,
      async () => {
        await this.ngForage.setItem(
          OfflineStorageTourService.getStorageKeyFor(dateString),
          subProjectWithTour.tour
        );
        this.subProjectHasChangedSubject.next({
          subProject: subProjectWithTour.subProject,
          changeType: changeType,
        });
      }
    );
  }

  private applyChangeToOtherTourSubProject<ParameterObject>(
    projectToOtherTour: ProjectToOtherTour,
    changeType: ChangeType,
    param: ParameterObject,
    changeFunction: (s: SubProject, param: ParameterObject) => void
  ): void {
    changeFunction(projectToOtherTour.subProject, param);
    this.projectToOtherTourStorageService
      .storeObject(projectToOtherTour)
      .then(() =>
        this.subProjectHasChangedSubject.next({
          subProject: projectToOtherTour.subProject,
          changeType: changeType,
        })
      );
  }

  public async getImmoTask(
    dateString: string,
    immoTaskId: number
  ): Promise<ImmoTask> {
    const toReturn = this.getFromOnline
      ? await this.getImmoTaskFromOnline(dateString, immoTaskId)
      : await this.getImmoTaskFromOffline(dateString, immoTaskId);
    if (toReturn != null) {
      await OfflineStorageTourHelper.applyImmoTaskChanges(
        [toReturn],
        this.immoIPImmoTaskChangesService
      );
    }
    return toReturn;
  }

  private async getImmoTaskFromOnline(
    dateString: string,
    immoTaskId: number
  ): Promise<ImmoTask> {
    const immoTask =
      await this.offlineStorageReadControllerRequestService.offlineStorageImmoTask(
        immoTaskId
      );
    if (immoTask != null) {
      SemaphoreHelper.use(
        OfflineStorageTourService.offlineStorageMutex,
        async () => {
          const storageKey =
            OfflineStorageTourService.getStorageKeyFor(dateString);
          const tour = await this.ngForage.getItem<Tour>(storageKey);
          if (tour != null && CollectionHelper.isPopulated(tour.tourItems)) {
            let immoTaskReplaced = false;
            for (const tourItem of tour.tourItems) {
              if (
                tourItem.immoTask != null &&
                tourItem.immoTask.id === immoTask.id
              ) {
                tourItem.immoTask = immoTask;
                immoTaskReplaced = true;
                break;
              }
            }
            if (immoTaskReplaced) {
              await this.ngForage.setItem(storageKey, tour);
              await this.ngForageTimestampService.setTimestamps(
                OfflineStorageTourService.timestampCollectionKey,
                [{ key: dateString, timestamp: Date.now() }]
              );
            }
          }
        }
      );
    }
    return immoTask;
  }

  async getImmoTaskFromOffline(
    dateString: string,
    immoTaskId: number
  ): Promise<ImmoTask> {
    const tour = await this.ngForage.getItem<Tour>(
      OfflineStorageTourService.getStorageKeyFor(dateString)
    );

    let toReturn = null;

    if (tour != null && CollectionHelper.isPopulated(tour.tourItems)) {
      for (const tourItem of tour.tourItems) {
        if (tourItem.immoTask != null && tourItem.immoTask.id === immoTaskId) {
          toReturn = tourItem.immoTask;
          break;
        }
      }
    }

    if (toReturn != null) {
      this.updateTimestamp(dateString);
    }

    return toReturn;
  }

  private async getImmoTaskWithTourFromOffline(
    dateString: string,
    immoTaskId: number
  ): Promise<[ImmoTask, Tour]> {
    const tour = await this.ngForage.getItem<Tour>(
      OfflineStorageTourService.getStorageKeyFor(dateString)
    );

    if (tour == null || !CollectionHelper.isPopulated(tour.tourItems)) {
      return [null, null];
    }

    let toReturn = null;

    for (const tourItem of tour.tourItems) {
      if (tourItem.immoTask != null && tourItem.immoTask.id === immoTaskId) {
        toReturn = tourItem.immoTask;
      }
    }

    if (toReturn != null) {
      this.updateTimestamp(dateString);
    }

    return [toReturn, tour];
  }

  public async saveImmoTaskChangeAndEmit<ParameterObject>(
    dateString: string,
    immoTaskId: number,
    changeType: ImmoTaskChangeType,
    param: ParameterObject,
    changeFunction: ImmoTaskEditFunction<ParameterObject>
  ): Promise<void> {
    const [immoTask, tour] = await this.getImmoTaskWithTourFromOffline(
      dateString,
      immoTaskId
    );

    if (immoTask != null) {
      changeFunction(immoTask, param);
      if (tour != null) {
        SemaphoreHelper.use(
          OfflineStorageTourService.offlineStorageMutex,
          async () => {
            await this.ngForage.setItem(
              OfflineStorageTourService.getStorageKeyFor(dateString),
              tour
            );
            this.immoTaskHasChangedSubject.next({
              immoTask: immoTask,
              changeType: changeType,
            });
          }
        );
      } else {
        this.immoTaskHasChangedSubject.next({
          immoTask: immoTask,
          changeType: changeType,
        });
      }
    }
  }

  public getSubProjectHasChangedObservable(): Observable<SubProjectWithChangeType> {
    return this.subProjectHasChangedSubject;
  }

  public getImmoTaskHasChangedObservable(): Observable<ImmoTaskWithChangeType> {
    return this.immoTaskHasChangedSubject;
  }
}

class ExpiredTourRemover {
  private readonly expiredItemRemovalInterval = 1800000; // 30 minutes

  constructor(
    private ngForageTimestampService: NgForageTimestampService,
    private ngForage: NgForage,
    private projectToTourStorageService: ProjectToTourStorageService
  ) {}

  async removeExpiredItems(): Promise<void> {
    const expiredItems = await this.ngForageTimestampService.getExpiredItems(
      OfflineStorageTourService.timestampCollectionKey
    );
    if (CollectionHelper.isPopulated(expiredItems)) {
      for (const i of expiredItems) {
        const expiredTour = await this.ngForage.getItem<Tour>(
          OfflineStorageTourService.getStorageKeyFor(i.key)
        );
        if (
          expiredTour != null &&
          CollectionHelper.isPopulated(expiredTour.tourItems)
        ) {
          this.handleExpiredTourItems(
            expiredTour.tourItems,
            DateHelper.formatDate(expiredTour.date, 'yyyyMMdd')
          );
        }
        await this.ngForage.removeItem(
          OfflineStorageTourService.getStorageKeyFor(i.key)
        );
      }
      this.ngForageTimestampService.removeItems(
        OfflineStorageTourService.timestampCollectionKey,
        expiredItems.map((i) => i.key)
      );
    }
    setTimeout(
      () => this.removeExpiredItems(),
      this.expiredItemRemovalInterval
    );
  }

  private async handleExpiredTourItems(
    tourItems: TourItem[],
    dateString: string
  ): Promise<void> {
    for (const tourItem of tourItems) {
      if (
        tourItem.subProject != null &&
        tourItem.subProject.project != null &&
        tourItem.subProject.project.id != null
      ) {
        const storageKey = this.projectToTourStorageService.getObjectStorageKey(
          tourItem.subProject.project.id
        );
        const projectToTour = await this.ngForage.getItem<ProjectToTour>(
          storageKey
        );
        if (projectToTour != null && projectToTour.dateString === dateString) {
          await this.projectToTourStorageService.removeObject(projectToTour);
        }
      }
    }
  }
}

interface SubProjectWithTour {
  subProject: SubProject;
  tour: Tour;
}

export interface SubProjectWithChangeType {
  subProject: SubProject;
  changeType: ChangeType;
}

export enum ChangeType {
  SUBMIT,
  ASSETS,
  REGISTERED_ACTIONS,
  MODIFY_CONFIG,
}

export interface ImmoTaskWithChangeType {
  immoTask: ImmoTask;
  changeType: ImmoTaskChangeType;
}

export enum ImmoTaskChangeType {
  SUBMIT,
  ASSETS,
  DELETE_ASSETS,
}
